import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { DropdownMenuTextOption } from '@alltrails/denali/types';
import CloseCircle from '@alltrails/denali/icons/CloseCircle';
import CloseLoop from '@alltrails/denali/icons/CloseLoop';
import DoubleBack from '@alltrails/denali/icons/DoubleBack';
import Reverse from '@alltrails/denali/icons/Reverse';
import HandWaving from '@alltrails/denali/icons/HandWaving';
import { DropdownMenuItem } from '@alltrails/denali/components/DropdownMenu';
import { type MarkerProperties } from '../../../types/CustomRoute';
import useSegmentActions from '../useSegmentActions';
import { useSelector } from '../../../redux';

type ContextMenuContentProps = MarkerProperties & {
  onClose: () => void;
};

const ContextMenuContent = ({ isStart, isEnd, onClose }: ContextMenuContentProps) => {
  const selectedHandleIndex = useSelector(state => state.customRoute.selectedHandleIndex);
  const {
    isReverseDisabled,
    isDoubleBackDisabled,
    isCloseLoopDisabled,
    reverse,
    doubleBack,
    closeLoop,
    selectStartPoint,
    selectEndPoint,
    removePoint
  } = useSegmentActions();

  const options = useMemo<DropdownMenuTextOption[]>(() => {
    // Shared options
    const reverseOption = {
      testId: 'context-menu-reverse',
      leadingIcon: { icon: { Component: Reverse } },
      title: <FormattedMessage defaultMessage="Reverse direction" />,
      onClick: async () => {
        await reverse('contextMenu');
        onClose();
      },
      disabled: isReverseDisabled
    };
    const doubleBackOption = {
      testId: 'context-menu-double-back',
      leadingIcon: { icon: { Component: DoubleBack } },
      title: <FormattedMessage defaultMessage="Double back" />,
      onClick: async () => {
        await doubleBack('contextMenu');
        onClose();
      },
      disabled: isDoubleBackDisabled
    };
    const closeLoopOption = {
      testId: 'context-menu-close-loop',
      leadingIcon: { icon: { Component: CloseLoop } },
      title: <FormattedMessage defaultMessage="Close loop" />,
      onClick: async () => {
        await closeLoop('contextMenu');
        onClose();
      },
      disabled: isCloseLoopDisabled
    };
    const removePointOption = {
      testId: 'context-menu-remove-point',
      leadingIcon: { icon: { Component: CloseCircle } },
      title: <FormattedMessage defaultMessage="Remove point" />,
      onClick: () => {
        removePoint();
        onClose();
      }
    };

    if (isStart && isEnd) {
      return [
        reverseOption,
        removePointOption,
        selectedHandleIndex !== 0
          ? {
              testId: 'context-menu-select-start',
              leadingIcon: { icon: { Component: HandWaving } },
              title: <FormattedMessage defaultMessage="Select starting point" />,
              onClick: () => {
                selectStartPoint();
                onClose();
              }
            }
          : {
              testId: 'context-menu-select-end',
              leadingIcon: { icon: { Component: HandWaving } },
              title: <FormattedMessage defaultMessage="Select endpoint" />,
              onClick: () => {
                selectEndPoint();
                onClose();
              }
            }
      ];
    }

    if (isStart || isEnd) {
      return [reverseOption, doubleBackOption, closeLoopOption, removePointOption];
    }

    return [removePointOption];
  }, [
    selectedHandleIndex,
    isStart,
    isEnd,
    onClose,
    isReverseDisabled,
    isDoubleBackDisabled,
    isCloseLoopDisabled,
    reverse,
    doubleBack,
    closeLoop,
    selectStartPoint,
    selectEndPoint,
    removePoint
  ]);

  return (
    <>
      {options.map(option => (
        <DropdownMenuItem key={option.testId} option={option} textVariant="text100" />
      ))}
    </>
  );
};

export default ContextMenuContent;
